/**
 * Set password (v2 frame "Hub · Set password"). Same column as sign-in. The rule ("at least 12
 * characters") is the placeholder, not a caption; an error appears under the field it belongs to.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Field } from '@buckmason/cx-ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTitle } from '../lib/useTitle';
import { AuthFrame } from './SignIn';

export function SetPassword() {
  useTitle('Set your password');
  const [params] = useSearchParams();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<{ field: 'password' | 'again'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== again) {
      setError({ field: 'again', text: 'The two passwords do not match.' });
      return;
    }
    setBusy(true);
    setError(null);
    let email: string;
    try {
      email = (await api.post<{ email: string }>('/api/id/auth/set-password', { token, password })).email;
    } catch (err) {
      setError({ field: 'password', text: err instanceof Error ? err.message : 'That did not work.' });
      setBusy(false);
      return;
    }
    // "Save and sign in": the new password signs them straight in. The link is used up now, so if
    // signing in fails (a blip, a deploy) say the password is set and send them to Sign in, rather
    // than an error here that a retry would turn into "this link was already used".
    try {
      await signIn(email, password);
      nav('/', { replace: true });
    } catch {
      nav('/sign-in', { replace: true, state: { passwordSet: true, email } });
    }
  };

  if (!token) {
    return (
      <AuthFrame>
        <header>
          <h1>This link is incomplete</h1>
          <p>Ask an admin for a new link.</p>
        </header>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <form className="auth-column-form" onSubmit={submit}>
        <header>
          <h1>Set your password</h1>
          <p>The link from your admin works once.</p>
        </header>

        <Field label="New password" error={error?.field === 'password' ? error.text : undefined}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            placeholder="At least 12 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Field label="Again" error={error?.field === 'again' ? error.text : undefined}>
          <input type="password" autoComplete="new-password" required value={again} onChange={(e) => setAgain(e.target.value)} />
        </Field>

        <button type="submit" className="primary btn-block" disabled={busy}>
          {busy ? 'Saving…' : 'Save and sign in'}
        </button>
      </form>
    </AuthFrame>
  );
}
